import React, { useState, useEffect, useMemo } from 'react';
import { Tooltip, Box, Fade, Slide, keyframes, useTheme } from '@mui/material';
import AIAssistant from './AIAssistant';
import { AI_COLORS } from '../../constants/aiColors';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

// Subtle glow animation
const subtleGlow = keyframes`
  0%, 100% {
    box-shadow: -2px 2px 8px rgba(147, 51, 234, 0.3);
  }
  50% {
    box-shadow: -2px 2px 14px rgba(147, 51, 234, 0.5);
  }
`;

const AIAssistantButton = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    // Determine if user has AI chat permission from AuthContext
    const hasAIChatPermission = useMemo(() => {
        if (user?.role?.name === 'Superadmin' || user?.role?.name === 'Admin') return true;
        return user?.role?.permissions?.ai_chat?.view === true;
    }, [user]);

    const handleToggle = () => {
        if (!hasAIChatPermission) {
            toast.error('AI chat is not enabled for your role. Contact your administrator to enable this feature.');
            return;
        }
        setOpen(prev => !prev);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const tooltipTitle = !hasAIChatPermission
        ? "AI chat not enabled for your role"
        : open ? "Close AI Assistant" : "Ask Enable AI";

    return (
        <>
            {/* Sticky tab on right edge - bottom position */}
            <Slide direction="left" in={!open} timeout={300}>
                <Tooltip
                    title={tooltipTitle}
                    placement="left"
                    arrow
                >
                    <Box
                        role="button"
                        aria-label="Ask Enable AI"
                        onClick={handleToggle}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        sx={{
                            position: 'fixed',
                            right: 0,
                            bottom: theme.spacing(12),
                            zIndex: 1299,
                            background: hasAIChatPermission ? AI_COLORS.gradient : '#cccccc',
                            animation: hasAIChatPermission ? `${subtleGlow} 3s ease-in-out infinite` : 'none',
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            borderRadius: '12px 0 0 12px',
                            padding: isHovered ? theme.spacing(1.25, 1.75) : theme.spacing(1.25, 1),
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            cursor: hasAIChatPermission ? 'pointer' : 'not-allowed',
                            borderLeft: '2px solid rgba(255, 255, 255, 0.25)',
                            borderTop: '2px solid rgba(255, 255, 255, 0.25)',
                            borderBottom: '2px solid rgba(255, 255, 255, 0.25)',
                            opacity: hasAIChatPermission ? 1 : 0.6,
                            '&:hover': {
                                padding: hasAIChatPermission ? theme.spacing(1.25, 1.75) : theme.spacing(1.25, 1),
                            },
                            '&:active': {
                                transform: hasAIChatPermission ? 'scale(0.96)' : 'scale(1)',
                            }
                        }}
                    >
                        <Box
                            component="img"
                            src="/ai-white.svg"
                            alt="AI"
                            sx={{
                                height: 20,
                                width: 20,
                                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))',
                            }}
                        />
                        {/* Show text only on hover */}
                        <Box
                            sx={{
                                color: theme.palette.common.white,
                                fontWeight: 600,
                                fontSize: '11px',
                                letterSpacing: '0.5px',
                                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                                fontFamily: theme.typography.fontFamily,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                maxWidth: isHovered ? '50px' : '0px',
                                opacity: isHovered ? 1 : 0,
                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            Enable AI
                        </Box>
                    </Box>
                </Tooltip>
            </Slide>

            <AIAssistant open={open} onClose={handleClose} />
        </>
    );
};

export default AIAssistantButton;
